import { useState } from 'react';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import {
  useSwrInstantlyActivateJob,
  useSwrSuspendJob,
  useSwrChangeJobState,
} from '@hooks/useSwrApi/endpoints/my-jobs';
import { useBjModal } from '@hooks/index';
import { useSwrSearchpackActivateJob } from '@hooks/useSwrApi/endpoints/my-jobs/useSwrSearchpackActivateJob';

type UseCompanyJobActionsProps = {
  jobSlug: string;
  jobId?: number;
  jobTitle?: string;
  eligibleForInstantActivation?: boolean;
  currentJobState: ApiSchema<'MyJobCard'>['state'];
  onSuspension?: () => void;
  onActivation?: () => void;
  suspensionType?: 'passive' | 'archived'
  onSuccesSearchpackActivation?: () => void;
  onArchiving?: () => void;
  onConfirmCancellation?: (suspensionType: 'passive' | 'archived') => void;
};

/**
 * @description useCompanyJobActions is a custom hook that provides functions to handle company job actions, such as activating and suspending jobs.
 * It uses SWR for data fetching and mutation, and it integrates with the router for navigation.
 * It also uses a custom modal hook (`useBjModal`) to manage the suspension confirmation modal state.
 *
 * Props:
 * * **jobSlug** - string - The slug of the job to be activated or suspended.
 * * **eligibleForInstantActivation** - boolean - Optional flag indicating if the job is eligible for instant activation.
 * * **currentJobState** - ApiSchema<'MyJobCard'>['state'] - Current state of the job (e.g., 'active', 'passive', 'archived').
 * * **onSuspension** - function - Optional callback function that is called when the job moved to passive successfully.
 * * **onActivation** - function - Optional callback function that is called when the job moved to active successfully.
 * * **onSuccesSearchpackActivation** - function - Optional callback function that is called when the job has activated a searchpack.
 * * **onArchiving** - function - Optional callback function that is called when the job moved to archived successfully.
 * * **onConfirmCancellation** - function - Optional callback function that is called when the job suspension confirmation modal is confirmed.
 *
 * Returns:
 * * **activateJob** - function to handle job activation, if instant activation is available it triggers SWR mutation and redirects
 * to after-activation page on success, otherwise it redirects to the edit page.
 * * **moveToPassive** - function to handle moving job to passive state, opens a confirmation modal.
 * * **moveToArchivedd** - function to handle moving job to archived state, opens a confirmation modal.
 * * **handleSuspensionConfirmation** - function to confirm job suspension, triggers the suspension SWR mutation.
 * * **handleSuspensionCancellation** - function to cancel job suspension, closes the confirmation modal.
 * * **isActivating** - boolean value indicating if the job activation is in progress.
 * * **isMovingToPassive** - boolean value indicating if the job is being moved to passive state.
 * * **isMovingToArchived** - boolean value indicating if the job is being moved to archived state.
 * * **isSuspensionConfirmationOpen** - boolean value indicating if the suspension confirmation modal is open.
 *
 * @example
 * ```tsx
 * const { activateJob, moveToPassive, moveToArchivedd, isActivating, isMovingToPassive, isMovingToArchived } = useCompanyJobActions({ job });
 * ```
 */

export function useCompanyJobActions(props: UseCompanyJobActionsProps) {
  const {
    jobSlug,
    jobTitle = '',
    eligibleForInstantActivation,
    currentJobState,
    onSuspension,
    onActivation,
    onSuccesSearchpackActivation,
    onArchiving,
    onConfirmCancellation,
  } = props;
  const { router } = useExtendedRouter();
  const [suspensionType, setSuspensionType] = useState<'passive' | 'archived'>('passive');

  const {
    isOpen: isSuspensionConfirmationOpen,
    openModal: openSuspensionConfirmationModal,
    closeModal: closeSuspensionConfirmationModal,
  } = useBjModal();

  const {
    isOpen: isSearchpackConfirmationOpen,
    openModal: openSearchpackConfirmationModal,
    closeModal: closeSearchpackConfirmationModal,
  } = useBjModal();


  const handleSearchpackCancel = () => {
    closeSearchpackConfirmationModal();
  };

  // SWR: Instantly activate a searchpack
  const { trigger: searchpackActivate, isMutating: isActivatingSearchpack } = useSwrSearchpackActivateJob({
    jobSlug,
    jobTitle,
    onSuccess: () => {
      closeSearchpackConfirmationModal();
      onSuccesSearchpackActivation?.();
    },
  });


  // SWR: Instantly activate a job
  const { trigger: instantlyActivateJob, isMutating: isActivating } = useSwrInstantlyActivateJob({
    jobSlug,
    onSuccess: () => {
      router.push(`/after-activation/${jobSlug}`);
      onActivation?.();
    },
  });

  // SWR: Suspend a job - Move active job to passive/archived state
  const { trigger: suspendJob, isMutating: isSuspending } = useSwrSuspendJob({
    jobSlug,
    onSuccess: () => {
      closeSuspensionConfirmationModal();
      if (suspensionType === 'passive') onSuspension?.();
      if (suspensionType === 'archived') onArchiving?.();
    },
  });

  // SWR: Suspend a job - Move active job to passive/archived state
  const { trigger: changeJobState, isMutating: isChangingJobState } = useSwrChangeJobState({
    slug: jobSlug,
    onSuccess: () => {
      closeSuspensionConfirmationModal();
      if (currentJobState === 'archived') onSuspension?.();
      if (currentJobState === 'passive') onArchiving?.();
    },
  });


  const openSearchpackConfirmation = () => {
    openSearchpackConfirmationModal();
  };

  const handleSearchpackConfirm = () => {
    void searchpackActivate({ jobSlug, jobTitle });
  };


  const activateJob = async () => {
    if (currentJobState === 'active') return;

    // Flow is the same no matter if job is in passive or archived state
    // If job is eligible for instant activation, activate it instantly
    // Otherwise, redirect to edit page for user to select package and activate
    if (eligibleForInstantActivation) {
      await instantlyActivateJob();
      return;
    }

    router.push(`/job-add/edit/${jobSlug}`);
  };

  const moveToPassive = async () => {
    if (currentJobState === 'passive') return;

    // When job is currently in active state, we move it to passive by suspending it (pass state: 'passive')
    if (currentJobState === 'active') {
      setSuspensionType('passive');
      openSuspensionConfirmationModal();
      return;
    }
    // When job is currently in archive state, we move it to passive by changing its state
    if (currentJobState === 'archived') {
      await changeJobState({ state: 'passive' });
    }
  };

  const moveToArchived = async () => {
    if (currentJobState === 'archived') return;

    // When job is currently in active state, we move it to archive by suspending it (pass state: 'archived')
    if (currentJobState === 'active') {
      setSuspensionType('archived');
      openSuspensionConfirmationModal();
      return;
    }
    // When job is currently in passive state, we move it to archive by changing its state
    if (currentJobState === 'passive') {
      await changeJobState({ state: 'archived' });
    }
  };

  const handleSuspensionConfirmation = async () => {
    await suspendJob({ state: suspensionType });
  };

  const handleSuspensionCancellation = () => {
    closeSuspensionConfirmationModal();
    onConfirmCancellation?.(suspensionType);
  };

  return {
    activateJob,
    moveToPassive,
    moveToArchived,
    handleSuspensionConfirmation,
    handleSuspensionCancellation,
    isActivating,
    suspensionType,
    isActivatingSearchpack,
    isMovingToPassive: currentJobState === 'active' ? isSuspending : isChangingJobState,
    isMovingToArchived: currentJobState === 'active' ? isSuspending : isChangingJobState,
    isSuspensionConfirmationOpen,
    isSearchpackConfirmationOpen,
    handleSearchpackConfirm,
    handleSearchpackCancel,
    openSearchpackConfirmation,
  };
}
